class YouMayAlsoLike extends HTMLElement {
  constructor() {
    super();

    if (!this.dataset.url) {
      this.finalize();
      return;
    }

    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      this.loadRecommendations();
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 200px 0px",
    }).observe(this);
  }

  get list() {
    return this.querySelector("[data-yml-list]");
  }

  get limit() {
    return parseInt(this.dataset.limit, 10) || 3;
  }

  get blend() {
    return this.dataset.blend === "true";
  }

  bucketOf(item) {
    return item.dataset.instore === "true" ? "instore" : "online";
  }

  visibleItems() {
    return Array.from(this.list.querySelectorAll("li:not([hidden])"));
  }

  loadRecommendations() {
    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const fetched = Array.from(
          html.querySelectorAll("you-may-also-like [data-yml-list] li")
        );
        this.merge(fetched);
      })
      .catch(() => {
        this.finalize();
      });
  }

  merge(fetched) {
    const list = this.list;
    if (!list) return;

    const handles = new Set(
      Array.from(list.querySelectorAll("li")).map((li) => li.dataset.handle)
    );
    const firstFallback = () => list.querySelector("li[data-fallback]");
    const leftovers = [];

    fetched.forEach((item) => {
      if (handles.has(item.dataset.handle)) return;
      if (this.visibleItems().length >= this.limit) {
        leftovers.push(item);
        return;
      }
      handles.add(item.dataset.handle);
      list.insertBefore(item, firstFallback());
    });

    this.finalize(leftovers);
  }

  enforceBlend(leftovers) {
    if (!this.blend) return;

    const list = this.list;
    const visible = this.visibleItems().slice(0, this.limit);
    if (visible.length < 2) return;

    const present = new Set(visible.map((item) => this.bucketOf(item)));
    if (present.size > 1) return;

    const missing = present.has("instore") ? "online" : "instore";
    const shown = new Set(visible.map((item) => item.dataset.handle));
    const pool = leftovers.concat(
      Array.from(list.querySelectorAll("li[hidden]"))
    );
    const candidate = pool.find(
      (item) =>
        this.bucketOf(item) === missing && !shown.has(item.dataset.handle)
    );
    if (!candidate) return;

    const victim = visible[visible.length - 1];
    list.insertBefore(candidate, victim);
    candidate.removeAttribute("hidden");
    victim.setAttribute("hidden", "");
  }

  finalize(leftovers = []) {
    const list = this.list;
    if (!list) return;

    list.querySelectorAll("li[data-fallback][hidden]").forEach((item) => {
      if (this.visibleItems().length < this.limit) item.removeAttribute("hidden");
    });

    this.enforceBlend(leftovers);

    this.visibleItems().forEach((item, index) => {
      if (index >= this.limit) item.setAttribute("hidden", "");
    });

    list.querySelectorAll("li[hidden]").forEach((item) => list.appendChild(item));

    if (this.visibleItems().length === 0) this.setAttribute("hidden", "");
  }
}

customElements.define("you-may-also-like", YouMayAlsoLike);
